<template>
    <!-- Shared Overlay for all popups -->
    <div v-if="allPopups.length > 0" class="global-popup-overlay" :style="computedOverlayStyle" @click="handleOverlayClick">
        <!-- Unified Popup Rendering -->
        <AnsPopup
            v-for="popup in allPopups"
            :key="popup.id"
            :is-visible="popup.isVisible"
            :title="popup.title"
            :logo-src="popup.logoSrc"
            :dimensions="popup.dimensions"
            :styles="popup.styles"
            :classes="popup.classes"
            :options="getPopupOptions(popup)"
            :custom-components="popup.customComponents"
            @close="handleClosePopup(popup)"
        >
            <component
                :is="popup.component"
                v-bind="popup.componentProps"
                @select="(data) => handleSelectPopup(popup, data)"
                @close="handleClosePopup(popup)"
                v-on="popup.eventHandlers"
            />
        </AnsPopup>
    </div>
</template>

<script>
/**
 * ****************************************************************************
 * @description     :   Global Popup Component
 * @created at      :   2025/08/01
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import { mapMutations, mapState } from 'vuex';
import AnsPopup from '@/components/ansPopup/index.vue';

export default {
    name: 'GlobalPopup',
    components: {
        AnsPopup
    },
    data() {
        return {
            scrollLocked: false,
            scrollbarWidth: 0
        };
    },
    mounted() {
        // Global ESC key handler for multiple popups
        document.addEventListener('keydown', this.handleGlobalEscapeKey);
    },
    unmounted() {
        document.removeEventListener('keydown', this.handleGlobalEscapeKey);
        // Restore body scroll when component is destroyed
        this.unlockScroll();
    },
    watch: {
        'allPopups.length': {
            handler(newLength) {
                this.$nextTick(() => {
                    if (newLength > 0 && !this.scrollLocked) {
                        this.lockScroll();
                    } else if (newLength === 0 && this.scrollLocked) {
                        this.unlockScroll();
                    }
                });
            },
            immediate: true
        }
    },
    computed: {
        ...mapState('app', {
            allPopups: (state) => state.popups,
            baseZIndex: (state) => state.baseZIndex
        }),
        computedOverlayStyle() {
            return {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: this.baseZIndex,
                transition: 'opacity 0.15s linear'
            };
        }
    },
    methods: {
        ...mapMutations('app', ['closePopupById']),

        lockScroll() {
            this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.overflow = 'hidden';
            if (this.scrollbarWidth > 0) {
                document.documentElement.style.paddingRight = `${this.scrollbarWidth}px`;
            }
            this.scrollLocked = true;
        },
        unlockScroll() {
            document.documentElement.style.overflow = '';
            document.documentElement.style.paddingRight = '';
            this.scrollLocked = false;
        },
        getTopmostPopup() {
            return this.allPopups.length > 0 ? this.allPopups[this.allPopups.length - 1] : null;
        },
        handleClosePopup(popup) {
            const customActionCloseButton = popup.options?.customActionCloseButton;

            if (customActionCloseButton?.isReplace) {
                customActionCloseButton?.callbacks?.();
            } else {
                const topmostPopup = this.getTopmostPopup();
                // Only allow closing the topmost popup
                if (topmostPopup && popup.id === topmostPopup.id) {
                    this.closePopupById(popup.id);
                }
                customActionCloseButton?.callbacks?.();
            }
        },
        handleOverlayClick() {
            const topmostPopup = this.getTopmostPopup();
            if (topmostPopup && topmostPopup.options?.closeOnOverlay !== false) {
                this.handleClosePopup(topmostPopup);
            }
        },
        getPopupOptions(popup) {
            // Disable closeOnOverlay for individual popups (handled by GlobalPopup overlay)
            return {
                ...popup.options,
                closeOnOverlay: false
            };
        },
        handleSelectPopup(popup, data) {
            if (popup.onSelect && typeof popup.onSelect === 'function') {
                popup.onSelect(data);
            }
        },
        handleGlobalEscapeKey(event) {
            if (event.key !== 'Escape') return;

            const popups = document.querySelectorAll('.popup-overlay');
            if (popups.length > 0) {
                return;
            }

            const topmostPopup = this.getTopmostPopup();
            if (topmostPopup && topmostPopup.options?.closeOnEscape !== false) {
                this.handleClosePopup(topmostPopup);
            }
        }
    }
};
</script>
